/**
 * useReportRenderers.ts
 *
 * Registers CopilotKit v2 tool renderers for all OneThing report tools.
 * Call inside CopilotToolRenderers (inside <CopilotKitProvider>). Returns nothing.
 */

import { useRenderTool } from "@copilotkit/react-core/v2";
import { createElement } from "react";
import { ReportListCard, type ReportListRow } from "../cards/ReportListCard";
import { ReportDetailCard, type ReportDetailData } from "../cards/ReportDetailCard";
import { MutationResultCard, type MutationData } from "../cards/MutationResultCard";
import {
  type ToolRenderProps,
  LoadingCard,
  ErrorCard,
  RawCard,
  resolveEnvelope,
} from "./renderHelpers";

// ---------------------------------------------------------------------------
// Render helpers
// ---------------------------------------------------------------------------

function renderReportList(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Loading reports…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok && env.kind === "report_list") {
    return createElement(ReportListCard, {
      summary: env.summary,
      rows: Array.isArray(env.data) ? (env.data as ReportListRow[]) : [],
    });
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

function renderReportDetail(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Loading report…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok && env.kind === "report_detail") {
    return createElement(ReportDetailCard, { data: env.data as ReportDetailData });
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

function renderReportMutation(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Submitting report…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok && env.kind === "report_mutation") {
    return createElement(MutationResultCard, {
      kind: "report_mutation",
      summary: env.summary,
      data: env.data as MutationData,
    });
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useReportRenderers(): void {
  // T14 — user's own reports → report_list
  useRenderTool({ name: "onething__list_reports", render: renderReportList }, []);
  // T15 — team reports → report_list
  useRenderTool({ name: "onething__team_reports", render: renderReportList }, []);
  // T16 — single report detail → report_detail
  useRenderTool({ name: "onething__get_report", render: renderReportDetail }, []);
  // T17 — submit report → report_mutation (reuses generic MutationResultCard)
  useRenderTool({ name: "onething__submit_report", render: renderReportMutation }, []);
}
